import * as THREE from 'three';
import { CONFIG } from '../../config.js';
import { Random } from '../../utils/Random.js';
import { BinaryMaterial } from '../../rendering/binary/BinaryMaterial.js';
import {
  applyDistanceSilhouette,
  pickArchetype,
  pickStreetFace,
  resolveArchetypeFootprint,
} from '../city/buildingArchetypes.js';
import { buildAlleyDetail, buildStreetLightsInBounds } from '../city/StreetEnvironment.js';
import { DetailedBuilding } from '../city/DetailedBuilding.js';
import { Road } from '../city/Road.js';
import { Sidewalk } from '../city/Sidewalk.js';
import { DigitalObject } from '../../rendering/binary/DigitalObject.js';
import {
  combineSeed,
  LOT_TYPE,
  pickLotType,
} from '../city/cityLayout.js';
import {
  deriveInteriorSeed,
  pickInteriorDesignIndex,
} from '../interiors/interiorDesigns.js';
import {
  blockStride,
  horizontalRoadCenterZ,
  lotCenterInBounds,
  lotDetailTierGlobal,
  lotWorldRectGlobal,
  rangeOverlaps,
  verticalRoadCenterX,
} from './worldGrid.js';

/**
 * Procedurally fills one streaming chunk region (deterministic global grid).
 */
export class CityRegionGenerator {
  /**
   * @param {{
   *   buildingMaterial: BinaryMaterial;
   *   accentMaterial: BinaryMaterial;
   *   accentPoleMaterial: BinaryMaterial;
   *   cityConfig: import('../../config.js').CONFIG['city'];
   *   bounds: import('./worldGrid.js').getChunkBounds extends Function ? never : { minX: number; maxX: number; minZ: number; maxZ: number };
   *   alloc: import('./ChunkManager.js').CellAllocator;
   *   signs: import('../city/DigitalSign.js').DigitalSign[];
   *   entranceRegistry?: import('../interiors/BuildingEntranceRegistry.js').BuildingEntranceRegistry;
   *   chunkKey?: string;
   * }} options
   */
  constructor({
    buildingMaterial,
    accentMaterial,
    accentPoleMaterial,
    cityConfig,
    bounds,
    alloc,
    signs,
    lod = 0,
    entranceRegistry = null,
    chunkKey = '',
  }) {
    this.buildingMaterial = buildingMaterial;
    this.accentMaterial = accentMaterial;
    this.accentPoleMaterial = accentPoleMaterial;
    this.config = cityConfig;
    this.bounds = bounds;
    this.alloc = alloc;
    this.signs = signs;
    this.lod = lod;
    this._entranceRegistry = entranceRegistry;
    this._chunkKey = chunkKey;
    this._entranceIndex = 0;

    this.colliders = [];
    this._roads = [];
    this._sidewalks = [];
    this._buildings = [];
    this._roadRoot = new THREE.Group();
    this._roadRoot.name = 'RoadNetwork';
    this.buildingCount = 0;
  }

  /**
   * @param {THREE.Group} intoGroup
   */
  build(intoGroup) {
    this._buildGround(intoGroup);
    this._buildRoadNetwork();
    this._buildStreetLights();
    this._buildLots();
    intoGroup.add(this._roadRoot);
    this.buildingCount = this._buildings.length;
  }

  dispose() {
    for (const road of this._roads) road.dispose();
    for (const sw of this._sidewalks) sw.dispose();
    for (const b of this._buildings) b.dispose();
    this._roads = [];
    this._sidewalks = [];
    this._buildings = [];
    this._geometries?.forEach((g) => g.dispose());
    this._materials?.forEach((m) => m.dispose());
  }

  _buildGround(intoGroup) {
    const { bounds } = this;
    const w = bounds.maxX - bounds.minX;
    const d = bounds.maxZ - bounds.minZ;
    const geo = new THREE.PlaneGeometry(w, d);
    const mat = new THREE.MeshStandardMaterial({
      color: CONFIG.matrix.darkGreen,
      roughness: 1,
      metalness: 0,
      emissive: CONFIG.matrix.primaryGreen,
      emissiveIntensity: 0.035,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.set(bounds.centerX, 0, bounds.centerZ);
    mesh.name = 'ChunkGround';
    intoGroup.add(mesh);
    this._geometries = [geo];
    this._materials = [mat];
    this.colliders.push({
      min: new THREE.Vector3(bounds.minX, -0.05, bounds.minZ),
      max: new THREE.Vector3(bounds.maxX, 0, bounds.maxZ),
      isFloor: true,
    });
  }

  _buildRoadNetwork() {
    const { bounds, config, lod } = this;
    const { roadWidth, sidewalkWidth } = config;
    const roadDensity = lod >= 2 ? 2 : lod >= 1 ? 2 : 3;
    const stride = blockStride(config);
    const zSpan = bounds.maxZ - bounds.minZ;
    const xSpan = bounds.maxX - bounds.minX;
    const centerZ = bounds.centerZ;
    const centerX = bounds.centerX;

    const iMin = Math.floor((bounds.minX - roadWidth / 2) / stride) - 1;
    const iMax = Math.ceil((bounds.maxX - roadWidth / 2) / stride) + 1;
    const jMin = Math.floor((bounds.minZ - roadWidth / 2) / stride) - 1;
    const jMax = Math.ceil((bounds.maxZ - roadWidth / 2) / stride) + 1;

    for (let i = iMin; i <= iMax; i += 1) {
      const roadX = verticalRoadCenterX(i, config);
      if (
        !rangeOverlaps(
          roadX - roadWidth / 2,
          roadX + roadWidth / 2,
          bounds.minX,
          bounds.maxX,
        )
      ) {
        continue;
      }
      this._addRoad({
        centerX: roadX,
        centerZ,
        width: roadWidth,
        depth: zSpan,
        seed: combineSeed(config.seed, i, bounds.chunkX ?? 0, 10),
        cellsPerMeter: roadDensity,
      });
      const swW = sidewalkWidth;
      this._addSidewalk({
        centerX: roadX - roadWidth / 2 - swW / 2,
        centerZ,
        width: swW,
        depth: zSpan,
        seed: combineSeed(config.seed, i, 1, 20),
        cellsPerMeter: roadDensity + 3,
      });
      this._addSidewalk({
        centerX: roadX + roadWidth / 2 + swW / 2,
        centerZ,
        width: swW,
        depth: zSpan,
        seed: combineSeed(config.seed, i, 2, 21),
        cellsPerMeter: roadDensity + 3,
      });
    }

    for (let j = jMin; j <= jMax; j += 1) {
      const roadZ = horizontalRoadCenterZ(j, config);
      if (
        !rangeOverlaps(
          roadZ - roadWidth / 2,
          roadZ + roadWidth / 2,
          bounds.minZ,
          bounds.maxZ,
        )
      ) {
        continue;
      }
      this._addRoad({
        centerX,
        centerZ: roadZ,
        width: xSpan,
        depth: roadWidth,
        seed: combineSeed(config.seed, 0, j, 11),
        cellsPerMeter: roadDensity,
      });
      this._addSidewalk({
        centerX,
        centerZ: roadZ - roadWidth / 2 - sidewalkWidth / 2,
        width: xSpan,
        depth: sidewalkWidth,
        seed: combineSeed(config.seed, 1, j, 22),
        cellsPerMeter: roadDensity + 3,
      });
      this._addSidewalk({
        centerX,
        centerZ: roadZ + roadWidth / 2 + sidewalkWidth / 2,
        width: xSpan,
        depth: sidewalkWidth,
        seed: combineSeed(config.seed, 2, j, 23),
        cellsPerMeter: roadDensity + 3,
      });
    }
  }

  _buildStreetLights() {
    if (this.lod >= 2) return;
    buildStreetLightsInBounds({
      bounds: this.bounds,
      config: this.config,
      material: this.accentMaterial,
      poleMaterial: this.accentPoleMaterial,
      roadRoot: this._roadRoot,
      spacing: this.lod >= 1 ? 40 : 28,
      maxLights: this.lod >= 1 ? 14 : 22,
      getCellIdBase: () => this.alloc.take(),
      onPlaced: (info) => this.alloc.report(info.cellsUsed, info.maxColumns, info.drawCalls),
    });
  }

  _buildLots() {
    const { bounds, config } = this;
    const stride = blockStride(config);
    const gxMin = Math.floor((bounds.minX - config.roadWidth) / stride);
    const gxMax = Math.ceil((bounds.maxX - config.roadWidth) / stride);
    const gzMin = Math.floor((bounds.minZ - config.roadWidth) / stride);
    const gzMax = Math.ceil((bounds.maxZ - config.roadWidth) / stride);

    for (let gz = gzMin; gz <= gzMax; gz += 1) {
      for (let gx = gxMin; gx <= gxMax; gx += 1) {
        const lot = lotWorldRectGlobal(gx, gz, config);
        if (!lotCenterInBounds(lot, bounds, 2.5)) continue;
        const seed = combineSeed(config.seed, gx, gz, 0);
        const rng = new Random(seed);
        const detail = lotDetailTierGlobal(gx, gz);
        this._fillLot(lot, gx, gz, rng, detail);
      }
    }
  }

  _fillLot(lot, gx, gz, rng, detail) {
    this._addLotPavement(lot, gx, gz);

    if (this.lod >= 2) {
      detail = { cellsPerMeter: 2, maxBuildings: 1, shape: 'facade' };
    } else if (this.lod >= 1) {
      detail = {
        ...detail,
        cellsPerMeter: Math.min(detail.cellsPerMeter, 4),
        maxBuildings: Math.min(detail.maxBuildings, 2),
      };
    }

    const type = pickLotType(rng);
    const pad = 1.1;
    const inner = {
      minX: lot.minX + pad,
      maxX: lot.maxX - pad,
      minZ: lot.minZ + pad,
      maxZ: lot.maxZ - pad,
      width: lot.width - pad * 2,
      depth: lot.depth - pad * 2,
      centerX: lot.centerX,
      centerZ: lot.centerZ,
    };

    if (type === LOT_TYPE.ALLEY) {
      if (this.lod >= 2) {
        this._spawnDetailed(
          inner.centerX,
          inner.centerZ,
          gx,
          gz,
          rng,
          detail,
          LOT_TYPE.NORMAL,
          combineSeed(this.config.seed, gx, gz, 101),
        );
        return;
      }
      const gap = 2.8;
      const wingWidth = (inner.width - gap) / 2 - 0.4;
      const height = 7 + rng.next() * 12;
      this._spawnDetailed(inner.minX + wingWidth / 2, inner.centerZ, gx, gz, rng, detail, type, combineSeed(this.config.seed, gx, gz, 101));
      this._spawnDetailed(inner.maxX - wingWidth / 2, inner.centerZ, gx, gz, rng, detail, type, combineSeed(this.config.seed, gx, gz, 102));
      const alley = buildAlleyDetail({
        material: this.accentMaterial,
        centerX: inner.centerX,
        centerZ: inner.centerZ,
        height,
        seed: combineSeed(this.config.seed, gx, gz, 105),
        cellIdBase: this.alloc.take(),
        signRegistry: this.signs,
      });
      this.alloc.report(alley.cellsUsed, alley.maxColumns, alley.drawCalls);
      this._roadRoot.add(alley.group);
      return;
    }

    if (type === LOT_TYPE.TOWER || type === LOT_TYPE.LARGE || type === LOT_TYPE.PLAZA) {
      this._spawnDetailed(inner.centerX, inner.centerZ, gx, gz, rng, detail, type, combineSeed(this.config.seed, gx, gz, 103));
      return;
    }

    const count = rng.int(1, detail.maxBuildings);
    for (let i = 0; i < count; i += 1) {
      const archetype = pickArchetype(rng, type);
      let spec = resolveArchetypeFootprint(archetype, inner, rng, detail);
      spec = applyDistanceSilhouette(spec, detail);
      const centerX = rng.range(inner.minX + spec.width / 2, inner.maxX - spec.width / 2);
      const centerZ = rng.range(inner.minZ + spec.depth / 2, inner.maxZ - spec.depth / 2);
      this._spawnWithSpec(centerX, centerZ, gx, gz, spec, combineSeed(this.config.seed, gx, gz, 200 + i));
    }
  }

  _addLotPavement(lot, gx, gz) {
    const inset = 0.15;
    const density = this.lod >= 2 ? 4 : this.lod >= 1 ? 5 : 7;
    const pad = new Sidewalk({
      material: this.buildingMaterial,
      centerX: lot.centerX,
      centerZ: lot.centerZ,
      width: lot.width - inset,
      depth: lot.depth - inset,
      cellsPerMeter: density,
      seed: combineSeed(this.config.seed, gx, gz, 44),
      cellIdBase: this.alloc.take(),
    });
    this.alloc.report(pad.cellsUsed, pad.maxColumns, 2);
    this._sidewalks.push(pad);
    this._roadRoot.add(pad.group);
  }

  _spawnDetailed(centerX, centerZ, gx, gz, rng, detail, lotType, seed) {
    const archetype = pickArchetype(rng, lotType);
    let spec = resolveArchetypeFootprint(archetype, { width: 16, depth: 16 }, rng, detail);
    spec = applyDistanceSilhouette(spec, detail);
    this._spawnWithSpec(centerX, centerZ, gx, gz, spec, seed);
  }

  _spawnWithSpec(centerX, centerZ, gx, gz, spec, seed) {
    if (CONFIG.interiors?.enabled) {
      spec = { ...spec, entrance: true };
    }
    const cellIdBase = this.alloc.take();
    const face = pickStreetFace(centerX, centerZ, this.config, new Random(seed + 99));
    const signRegistry =
      this.lod >= 2 || CONFIG.graphics?.minimalBuildingProps
        ? null
        : this.signs;
    const building = new DetailedBuilding({
      material: this.buildingMaterial,
      accentMaterial: this.accentMaterial,
      centerX,
      centerZ,
      spec,
      seed,
      cellIdBase,
      streetFace: face,
      signRegistry: signRegistry ?? [],
    });
    this.alloc.report(building.cellsUsed, building.maxColumns, building.drawCalls);
    this._buildings.push(building);
    this.colliders.push(building.getCollider());
    this._roadRoot.add(building.group);

    if (
      this._entranceRegistry &&
      this._chunkKey &&
      building.entranceRecord &&
      CONFIG.interiors?.enabled
    ) {
      const slot = this._entranceIndex;
      const interiorSeed = deriveInteriorSeed(
        this.config.seed,
        gx,
        gz,
        seed,
        slot,
      );
      const record = {
        ...building.entranceRecord,
        id: `${this._chunkKey}:${slot}`,
        chunkKey: this._chunkKey,
        interiorSeed,
        designIndex: pickInteriorDesignIndex(interiorSeed),
      };
      this._entranceIndex += 1;
      this._entranceRegistry.register(record);
    }
  }

  _addRoad(params) {
    const cellIdBase = this.alloc.take();
    const road = new Road({ material: this.buildingMaterial, cellIdBase, ...params });
    this.alloc.report(road.cellsUsed, road.maxColumns, 1);
    this._roads.push(road);
    this._roadRoot.add(road.group);

    if (this.lod <= 1 && params.width >= 6) {
      const stripe = new DigitalObject({
        material: this.accentMaterial,
        shape: 'wall',
        size: [Math.max(0.35, params.width * 0.06), 0.025, params.depth],
        position: [params.centerX, 0.028, params.centerZ],
        cellsPerMeter: 8,
        seed: (params.seed ?? 0) + 909,
        cellIdBase: this.alloc.take(),
        name: 'RoadCenterStripe',
      });
      this.alloc.report(stripe.cellsUsed, stripe.surfaces[0]?.columns ?? 4, 1);
      this._roadRoot.add(stripe.group);
    }
  }

  _addSidewalk(params) {
    const cellIdBase = this.alloc.take();
    const sidewalk = new Sidewalk({
      material: this.buildingMaterial,
      cellIdBase,
      ...params,
    });
    this.alloc.report(sidewalk.cellsUsed, sidewalk.maxColumns, 1);
    this._sidewalks.push(sidewalk);
    this._roadRoot.add(sidewalk.group);
  }
}
